import { supabase } from "@/integrations/supabase/client";

/**
 * Extract mentioned usernames from text content
 * Returns array of usernames (without @ symbol)
 */
export const extractMentions = (text: string): string[] => {
  const mentionRegex = /@(\w+)/g;
  const mentions: string[] = [];
  let match;

  while ((match = mentionRegex.exec(text)) !== null) {
    mentions.push(match[1]);
  }

  return mentions;
};

/**
 * Save mentions to database and create notifications
 */
export const saveMentions = async (
  content: string,
  contentType: "message" | "discussion" | "discussion_reply" | "support_ticket",
  contentId: string
) => {
  const mentionedUsernames = extractMentions(content);
  
  if (mentionedUsernames.length === 0) return;

  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return;

  // Get user IDs for mentioned usernames
  const { data: mentionedUsers } = await supabase
    .from("profiles")
    .select("id, username")
    .in("username", mentionedUsernames);

  if (!mentionedUsers || mentionedUsers.length === 0) return;

  // Create mentions records
  const mentionsData = mentionedUsers.map((mentionedUser) => ({
    mentioned_user_id: mentionedUser.id,
    mentioning_user_id: user.id,
    content_type: contentType,
    content_id: contentId,
  }));

  const { error: mentionsError } = await supabase
    .from("mentions")
    .insert(mentionsData);

  if (mentionsError) {
    console.error("Error saving mentions:", mentionsError);
    return;
  }

  // Get mentioning user's username
  const { data: mentioningUserProfile } = await supabase
    .from("profiles")
    .select("username")
    .eq("id", user.id)
    .single();

  // Create notifications for mentioned users
  const notificationsData = mentionedUsers.map((mentionedUser) => ({
    user_id: mentionedUser.id,
    title: "You were mentioned",
    message: `${mentioningUserProfile?.username || "Someone"} mentioned you in a ${contentType.replace("_", " ")}`,
    type: "mention",
    related_id: contentId,
  }));

  await supabase.from("notifications").insert(notificationsData);
};

/**
 * Get all mentions for current user
 */
export const getUserMentions = async () => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return [];

  const { data } = await supabase
    .from("mentions")
    .select("*, profiles!mentions_mentioning_user_id_fkey(username)")
    .eq("mentioned_user_id", user.id)
    .order("created_at", { ascending: false });

  return data || [];
};
